/**
 * "What should your model do next?" — SaaS customer-intents modal
 * (Figma 9036-10012) and the "Contact us" tooltips that open it
 * (Figma 9035-9521).
 *
 * On SaaS only Classification and the non-agentic autonomy level are
 * self-serve; the other task types / levels are disabled and point here.
 */
import { TaskType } from '@/shared/domain/taskTypes';

export const CUSTOMER_INTENT_MODAL_WIDTH = 600;
export const CUSTOMER_INTENT_MODAL_TITLE = 'What should your model do next?';

/**
 * Tooltip copy for gated controls: "<prefix> Contact us", where the trailing
 * "Contact us" is rendered as the link (``ContactUsTooltipContent``).
 */
export const CONTACT_US_LINK_LABEL = 'Contact us';
export const CONTACT_US_TASK_TYPE_PREFIX = 'To enable this task type';
export const CONTACT_US_AGENTIC_PREFIX = 'To enable agentic capabilities';
export const CONTACT_US_TASK_TYPE = `${CONTACT_US_TASK_TYPE_PREFIX} ${CONTACT_US_LINK_LABEL}`;
export const CONTACT_US_AGENTIC = `${CONTACT_US_AGENTIC_PREFIX} ${CONTACT_US_LINK_LABEL}`;

export const CUSTOMER_INTENT_FEATURES_LABEL = 'Tell us which features you would like to use';
export const CUSTOMER_INTENT_AUTONOMY_LABEL = 'Autonomy level you would like to reach';
export const CUSTOMER_INTENT_SOMETHING_ELSE_LABEL = 'Something else..';
export const CUSTOMER_INTENT_SOMETHING_ELSE_PLACEHOLDER = 'What are you trying to build?';
/** Mirrors ``MAX_SOMETHING_ELSE_LENGTH`` in ``backend/app/customer_intents/service.py``. */
export const CUSTOMER_INTENT_SOMETHING_ELSE_MAX_LENGTH = 5000;

/**
 * Feature cards (multi-select). `value` is the ``RequestedFeature`` wire enum;
 * `iconType` is the Spark ``TaskTypeIcon`` tile shown on the card.
 */
export const CUSTOMER_INTENT_FEATURES = [
    {
        value: 'entity_extraction',
        label: 'Entity Extraction',
        description: 'Pull names, dates and other spans out of text',
        iconType: 'extraction',
    },
    {
        value: 'summarization',
        label: 'Summarization',
        description: 'Condense long documents into short summaries',
        iconType: 'summarization',
    },
    {
        value: 'content_generation',
        label: 'Content Generation',
        description: 'Write new text from your instructions',
        iconType: 'content-generation',
    },
];

/** Autonomy tiles (multi-select). `value` is the ``RequestedAutonomyLevel`` wire enum. */
export const CUSTOMER_INTENT_AUTONOMY_LEVEL_NONE = 'none';
export const CUSTOMER_INTENT_AUTONOMY_LEVELS = [
    { value: CUSTOMER_INTENT_AUTONOMY_LEVEL_NONE, label: "I don't need it" },
    { value: 'one_tool_call', label: 'Agent has one tool call per intent' },
    { value: 'agent_decides', label: 'Agent decides which tools to call' },
    { value: 'open_ended', label: 'Agent can explore, open-ended goal' },
];

/** Gated wizard task type (``TaskType``) → the feature card to pre-check when its "Contact us" is clicked. */
export const CUSTOMER_INTENT_FEATURE_BY_TASK_TYPE = {
    [TaskType.NER]: 'entity_extraction',
    [TaskType.summarization]: 'summarization',
    [TaskType.contentGeneration]: 'content_generation',
};

/**
 * Gated wizard autonomy level (2–4, see ``businessGoals/autonomyLevels.js``) →
 * the autonomy tile to pre-check when its "Contact us" is clicked.
 */
export const CUSTOMER_INTENT_AUTONOMY_BY_TASK_LEVEL = {
    2: 'one_tool_call',
    3: 'agent_decides',
    4: 'open_ended',
};

export const EMPTY_CUSTOMER_INTENT_SELECTION = Object.freeze({ features: [], autonomyLevels: [] });

/** Initial modal selection for the "Contact us" link of a gated task type. */
export const intentForTaskType = (taskType) => {
    const feature = CUSTOMER_INTENT_FEATURE_BY_TASK_TYPE[taskType];
    return feature ? { features: [feature] } : {};
};

/** Initial modal selection for the "Contact us" link of a gated autonomy level. */
export const intentForAutonomyLevel = (level) => {
    const autonomyLevel = CUSTOMER_INTENT_AUTONOMY_BY_TASK_LEVEL[Number(level)];
    return autonomyLevel ? { autonomyLevels: [autonomyLevel] } : {};
};

/** Coerces whatever a "Contact us" handler was called with into `{features, autonomyLevels}`. */
export const normalizeCustomerIntentSelection = (initial) => ({
    features: Array.isArray(initial?.features) ? initial.features : [],
    autonomyLevels: Array.isArray(initial?.autonomyLevels) ? initial.autonomyLevels : [],
});

export const CUSTOMER_INTENT_SENT_MESSAGE =
    "Thanks! We'll be in touch about enabling these capabilities.";
export const CUSTOMER_INTENT_SEND_FAILED_MESSAGE = 'Could not send your request. Please try again.';

/**
 * "I want to deploy platform on my cloud" (Need a Dataset? menu, SaaS only).
 * Success toast per Figma 9293-3254.
 */
export const CLOUD_DEPLOY_SENT_TITLE = 'We got your request!';
export const CLOUD_DEPLOY_SENT_MESSAGE =
    'We will be in touch with you soon to assist you deploy platform on your cloud';

/**
 * Whether a submission carries anything worth filing — a feature, an agentic
 * autonomy level, or non-blank free text. Mirrors the backend's 422 rule so
 * the Send button is disabled instead of surfacing a validation error.
 */
export const hasCustomerIntentContent = ({ features, autonomyLevels, somethingElse }) =>
    (features?.length ?? 0) > 0 ||
    (autonomyLevels ?? []).some((level) => level !== CUSTOMER_INTENT_AUTONOMY_LEVEL_NONE) ||
    Boolean(somethingElse?.trim());

/**
 * Toggles an autonomy tile. "I don't need it" is exclusive with the agentic
 * levels, which can be combined freely.
 */
export const toggleCustomerIntentAutonomyLevel = (levels, value) => {
    if (levels.includes(value)) return levels.filter((level) => level !== value);
    if (value === CUSTOMER_INTENT_AUTONOMY_LEVEL_NONE) return [value];
    return [...levels.filter((level) => level !== CUSTOMER_INTENT_AUTONOMY_LEVEL_NONE), value];
};
